import React, { useContext, useMemo, type JSX } from 'react';
import EDView from '../../../../../../../BaseComponents/View/EDView';
import EDTypography from '../../../../../../../BaseComponents/Typography/EDTypography';
import { findFormatter } from '../../../../../../../Utils/Formatters/FindFormatter';
import { trimTrailingDashes } from './Utils/KonteringUtils';
import { Theme } from '@mui/material';
import { makeStyles } from 'tss-react/mui';
import {
  TEDLRFakturaAttestAttestTypEnum,
  TEDLRFakturaAttestSTATUSEnum,
} from '../../../../../../../globalQueryTypes';
import { FakturaDelQuery_node_TEDLRFakturaDel__Associations_Fakturaattest_All } from '../../Queries/_QueryTypes/FakturaDelQuery';
import EDIcon, {
  IconColor,
} from '../../../../../../../BaseComponents/Icon/EDIcon';
import { useIsSmallScreen } from '../../../../../../../Hooks/useFullscreenView';
import FakturaDelContext from '../FakturaDelContextManager/FakturaDelContext';
import { IFieldValidation } from '../../../../../_Common/Components/BusinessCollectionComponents/BusinessDataForm/Utils/BusinessDataFormUtils';
import EDTooltip from '../../../../../../../BaseComponents/Tooltip/EDTooltip';
import EDBox from '../../../../../../../BaseComponents/EDBox/EDBox';
import { isTillaggsinfoError } from './Components/Tillaggsinfo/Utils/TillaggsinfoUtils';

interface IProps {
  attestTyp: TEDLRFakturaAttestAttestTypEnum | null;
  belopp: number;
  beloppExklMoms: number | null | undefined;
  beloppInklMoms: number | null | undefined;
  beslutsattestanter: FakturaDelQuery_node_TEDLRFakturaDel__Associations_Fakturaattest_All[];
  beslutsAttestant?: FakturaDelQuery_node_TEDLRFakturaDel__Associations_Fakturaattest_All | null;
  disabled?: boolean;
  infoMissingMessage?: string | null;
  isBeslutsattestantOrReserv?: boolean;
  kodstrang: string;
  kodstrangBenamningar: string[];
  konteringsforslagSaknas: boolean;
  onBeslutsattestantChanged: (
    fakturaAttest: FakturaDelQuery_node_TEDLRFakturaDel__Associations_Fakturaattest_All | null
  ) => void;
  periodiseringTomDatum?: string | null;
  periodiseringFromDatum?: string | null;
  showTillaggsInfo: boolean;
  showPeriodIcon: boolean;
  statusIcon?: JSX.Element;
  validationErrors?: IFieldValidation[];
  radtyp: number | null | undefined;
  konteringsForslagIndex?: number;
}

const useStyles = makeStyles()((theme: Theme) => ({
  view: {
    width: '100%',
    padding: 0,
  },
  columnContainer: {
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'space-between',
    padding: theme.spacing(0, 1),
  },
  container: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    width: '100%',
  },
  infoContainer: {
    display: 'flex',
    alignItems: 'center',
  },
  iconContainer: {
    marginLeft: theme.spacing(1),
    marginRight: theme.spacing(-1),
  },
  konteringsForslagSaknasText: {
    color: theme.palette.warning.main,
  },
  kodstrangBenamningar: {
    padding: theme.spacing(0.125, 1),
    fontSize: '0.75rem',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    width: '95%',
  },
  label: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: theme.spacing(3),
  },
  attestantLabelContainer: {
    alignItems: 'center',
    display: 'flex',
    flexFlow: 'row nowrap',
    padding: theme.spacing(0, 1, 0, 2),
    justifyContent: 'space-between',
  },
  missingError: {
    color: theme.palette.error.dark,
  },
  missingWarning: {
    color: theme.customProperties.Warning.dark,
  },
  beloppContainer: {
    width: 'auto',
  },
}));

interface IAttestantProps {
  attestant?: FakturaDelQuery_node_TEDLRFakturaDel__Associations_Fakturaattest_All;
  attesterad: boolean;
  className: string;
}

const AttestantLabel: React.FC<
  React.PropsWithChildren<IAttestantProps>
> = props => {
  const fakturaDelContext = useContext(FakturaDelContext);

  const attestantPrefix = !props.attesterad
    ? props.attestant?.Attestant
      ? 'Beslutsattesteras av: '
      : ''
    : 'Beslutsattesteras av: ';

  let text = props.attestant?.Attestant ?? 'Beslutsattestant saknas';

  if (props.attestant) {
    if (fakturaDelContext.innehallerFleraFakturadelar) {
      const attestDel =
        fakturaDelContext.beslutsAttestanterAllaFakturadelar.filter(
          ba => ba.id === props.attestant?.id
        )?.[0];

      const fakturadel = fakturaDelContext.getFakturadel(attestDel);

      text = text + ` (del ${fakturadel?.ORDNINGS_NR})`;
    }
  }

  return (
    <EDBox
      component="span"
      sx={{ display: 'flex', flexFlow: 'row nowrap', alignItems: 'center' }}
    >
      {props.attesterad && <EDIcon iconType="check" color="primary" />}

      <EDTypography>
        {attestantPrefix}
        <EDTypography
          bold={Boolean(props.attestant)}
          component="span"
          classes={{ ['root']: !props.attestant ? props.className : '' }}
          sx={{ whiteSpace: 'nowrap' }}
        >
          {text}
        </EDTypography>
      </EDTypography>
    </EDBox>
  );
};

const KonteringLabel: React.FC<React.PropsWithChildren<IProps>> = props => {
  const isSmallScreen = useIsSmallScreen();
  const { classes, cx } = useStyles();

  const showStatusIcons = Boolean(props.statusIcon);

  const currencyFormatter = findFormatter('TEDFormatCurr');

  const additionalInformationIconColor: IconColor | undefined = useMemo(
    () =>
      props.validationErrors
        ?.filter(isTillaggsinfoError)
        .reduce((prev: IconColor, curr) => {
          if (curr.type === 'Error') {
            return 'error';
          }

          if (curr.type === 'Warning' && prev !== 'error') {
            return 'warning';
          }

          return prev;
        }, 'secondary'),
    [props.validationErrors]
  );

  const getBeloppAndKostnad = (
    belopp: number | null | undefined,
    kostnad: number
  ) => {
    return (
      <>
        <EDTypography>
          {'Belopp: '}
          {!isSmallScreen && (
            <>
              <EDTypography
                testId={`kontering-${props.konteringsForslagIndex}-rubrikbelopp`}
                bold
                sx={{ display: 'inline-block' }}
              >
                {`${currencyFormatter.displayer.display(belopp, true)} `}
              </EDTypography>
              <EDTypography
                testId={`kontering-${props.konteringsForslagIndex}-kostnadbelopp`}
                sx={{ display: 'inline-block', marginLeft: '5px' }}
              >
                {`(${currencyFormatter.displayer.display(kostnad, true)})`}
              </EDTypography>
            </>
          )}
        </EDTypography>
        {isSmallScreen && (
          <>
            <EDTypography
              testId={`kontering-${props.konteringsForslagIndex}-rubrikbelopp`}
              bold
            >
              {`${currencyFormatter.displayer.display(belopp, true)} `}
            </EDTypography>
            <EDTypography
              testId={`kontering-${props.konteringsForslagIndex}-kostnadbelopp`}
            >
              {`(${currencyFormatter.displayer.display(kostnad, true)})`}
            </EDTypography>
          </>
        )}
      </>
    );
  };

  const getBeloppExklKostnad = (belopp: number | null | undefined) => {
    return (
      <>
        <EDTypography>
          {'Belopp: '}
          {!isSmallScreen && (
            <>
              <EDTypography
                testId={`kontering-${props.konteringsForslagIndex}-rubrikbelopp`}
                bold
                sx={{ display: 'inline-block' }}
              >
                {`${currencyFormatter.displayer.display(belopp, true)} `}
              </EDTypography>
            </>
          )}
        </EDTypography>
        {isSmallScreen && (
          <>
            <EDTypography
              testId={`kontering-${props.konteringsForslagIndex}-rubrikbelopp`}
              bold
            >
              {`${currencyFormatter.displayer.display(belopp, true)} `}
            </EDTypography>
          </>
        )}
      </>
    );
  };

  const getLabelBelopp = (radtyp: number | null | undefined) => {
    switch (radtyp) {
      case 0:
      case 1:
      case 2:
      case null:
        return (
          <>
            <EDTypography>
              {'Belopp: '}
              {!isSmallScreen && (
                <EDTypography
                  testId={`kontering-${props.konteringsForslagIndex}-rubrikbelopp`}
                  bold
                  component="span"
                >
                  {currencyFormatter.displayer.display(props.belopp, true)}
                </EDTypography>
              )}
            </EDTypography>
            {isSmallScreen && (
              <EDTypography
                testId={`kontering-${props.konteringsForslagIndex}-rubrikbelopp`}
                bold
              >
                {currencyFormatter.displayer.display(props.belopp, true)}
              </EDTypography>
            )}
          </>
        );

      case 3:
        return getBeloppExklKostnad(props.beloppInklMoms);

      case 4:
        return getBeloppAndKostnad(props.beloppInklMoms, props.belopp);

      case 5:
      case 6:
        return getBeloppAndKostnad(props.beloppExklMoms, props.belopp);

      default:
        return <></>;
    }
  };

  const periodiseradTooltip = useMemo(() => {
    if (
      props.showPeriodIcon &&
      (props.periodiseringFromDatum || props.periodiseringTomDatum)
    ) {
      return `Periodiserad ${props.periodiseringFromDatum ?? ''} - ${
        props.periodiseringTomDatum ?? ''
      }`;
    }
    return 'Periodiserad - datumintervall saknas';
  }, [
    props.periodiseringFromDatum,
    props.periodiseringTomDatum,
    props.showPeriodIcon,
  ]);

  return (
    <>
      {showStatusIcons && (
        <span className={classes.label}>
          <EDTooltip
            title={
              props.validationErrors?.map(error => (
                <>
                  <span key={error.fieldName}>{error.message}</span>
                  <br />
                </>
              )) ?? ''
            }
            enableClickOnTouch={Boolean(props.validationErrors?.length)}
          >
            <span className={classes.iconContainer}>{props.statusIcon}</span>
          </EDTooltip>
        </span>
      )}

      <EDView gutter className={classes.view}>
        <div className={classes.columnContainer}>
          <div className={classes.container}>
            <div className={classes.columnContainer}>
              <span className={classes.infoContainer}>
                <EDTypography
                  bold={!props.konteringsforslagSaknas}
                  classes={{
                    ['root']: props.konteringsforslagSaknas
                      ? props.attestTyp ===
                          TEDLRFakturaAttestAttestTypEnum.BESLUTSATTEST &&
                        props.isBeslutsattestantOrReserv
                        ? classes.missingError
                        : classes.missingWarning
                      : '',
                  }}
                >
                  {props.konteringsforslagSaknas
                    ? props.infoMissingMessage
                    : trimTrailingDashes(props.kodstrang)}
                </EDTypography>
              </span>
            </div>
            <div
              className={cx([classes.columnContainer, classes.beloppContainer])}
              data-testid={`kontering-${props.konteringsForslagIndex}-Belopp-container`}
            >
              {getLabelBelopp(props.radtyp)}
            </div>
          </div>
          {!isSmallScreen && (
            <EDTypography className={classes.kodstrangBenamningar}>
              {trimTrailingDashes(props.kodstrangBenamningar.join('-'))}
            </EDTypography>
          )}
        </div>

        <div className={classes.attestantLabelContainer}>
          <EDBox component="span">
            <AttestantLabel
              attesterad={
                props.beslutsAttestant?.STATUS ===
                TEDLRFakturaAttestSTATUSEnum.ATTESTERAD
              }
              attestant={props.beslutsAttestant ?? undefined}
              className={classes.missingWarning}
            />
          </EDBox>
          <EDBox
            sx={{
              display: 'flex',
              gap: props.showTillaggsInfo && props.showPeriodIcon ? 1 : 0,
            }}
          >
            {props.showPeriodIcon && (
              <EDTooltip title={periodiseradTooltip}>
                <EDBox component="span">
                  <EDIcon
                    color={additionalInformationIconColor}
                    iconType="access_time"
                  />
                </EDBox>
              </EDTooltip>
            )}
            {props.showTillaggsInfo && (
              <EDTooltip title="Tilläggsinformation">
                <EDBox component="span">
                  <EDIcon
                    color={additionalInformationIconColor}
                    iconType="additionalinfo_filled"
                  />
                </EDBox>
              </EDTooltip>
            )}
          </EDBox>
        </div>
      </EDView>
    </>
  );
};

export default KonteringLabel;
